import { CSSProperties, Fragment } from 'react';
import clsx from 'clsx';
import { IColumnGroupBand } from '../dataTableColumnGroups';

export interface IColumnGroupSticky {
    className?: string;
    style?: CSSProperties;
}

interface IDataTableColumnGroupsRowProps {
    bands: IColumnGroupBand[];
    /** Служебные треки перед колонками данных (ручка, чекбокс, действия, разворот) по порядку. */
    leadTracks: string[];
    /** Трек действий справа. */
    actionsTrack?: string;
    /** Колонка, перед которой в сетке стоит распорка. */
    spacerBeforeColId: string | null;
    spacerBeforeActions: boolean;
    trailingSpacer: boolean;
    colIndexOf: (id: string) => number | undefined;
    /** Закрепление ячейки над треками `ids` — то же, что у ячеек шапки под ними. */
    stickyOf: (ids: string[]) => IColumnGroupSticky;
}

const Spacer = () => <div className="dtb__colgroup dtb__elastic-spacer" />;

/**
 * Строка групп колонок над шапкой. Сетка та же, что у шапки (`--dtb-cols`), а ячейка
 * группы занимает span своих колонок — ширина группы равна сумме их треков и следует
 * за ресайзом без пересчёта. Над служебными треками и колонками вне групп — пустые
 * ячейки: закреплённые из них закрывают едущие под ними подписи.
 */
const DataTableColumnGroupsRow = ({
    bands,
    leadTracks,
    actionsTrack,
    spacerBeforeColId,
    spacerBeforeActions,
    trailingSpacer,
    colIndexOf,
    stickyOf,
}: IDataTableColumnGroupsRowProps) => {
    const cell = (ids: string[], label?: string) => {
        const sticky = stickyOf(ids);
        return (
            <div
                className={clsx('dtb__colgroup', label && 'dtb__colgroup--label', sticky.className)}
                style={ids.length > 1 ? { ...sticky.style, gridColumn: `span ${ids.length}` } : sticky.style}
                role={label ? 'columnheader' : undefined}
                aria-colindex={label ? colIndexOf(ids[0]) : undefined}
                aria-colspan={label ? ids.length : undefined}
            >
                {label && (
                    <span className="dtb__colgroup-text" title={label}>
                        {label}
                    </span>
                )}
            </div>
        );
    };
    return (
        <div className="dtb__colgroups" role="row">
            {leadTracks.map((id) => (
                <Fragment key={id}>{cell([id])}</Fragment>
            ))}
            {bands.map(({ label, columnIds }) => (
                <Fragment key={columnIds[0]}>
                    {columnIds[0] === spacerBeforeColId && <Spacer />}
                    {cell(columnIds, label)}
                </Fragment>
            ))}
            {spacerBeforeActions && <Spacer />}
            {actionsTrack && cell([actionsTrack])}
            {trailingSpacer && <Spacer />}
        </div>
    );
};

export default DataTableColumnGroupsRow;
